import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createLightTheme, createDarkTheme } from "../theme/theme";

// Define available theme modes
export type ThemeMode = "light" | "dark" | "system";

// Theme context interface
interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleColorMode: () => void;
  actualTheme: "light" | "dark";
}

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  mode: "system",
  setMode: () => {},
  toggleColorMode: () => {},
  actualTheme: "light",
});

// Custom hook to use theme context
export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Get saved theme from localStorage or use system as default
  const savedTheme = localStorage.getItem("theme-mode") as ThemeMode;
  const [mode, setMode] = useState<ThemeMode>(savedTheme || "system");

  // Determine if the system prefers dark mode
  const prefersDarkMode = window.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  // Calculate the actual theme based on mode and system preference
  const actualTheme =
    mode === "system" ? (prefersDarkMode ? "dark" : "light") : mode;

  // Create light and dark themes
  const lightTheme = createLightTheme();
  const darkTheme = createDarkTheme();

  // Set theme in localStorage when mode changes
  useEffect(() => {
    localStorage.setItem("theme-mode", mode);
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      if (mode === "system") {
        // Force a re-render when system preference changes and we're using system mode
        setMode("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mode]);

  // Toggle between light and dark mode
  const toggleColorMode = () => {
    setMode((prevMode) => {
      if (prevMode === "light") return "dark";
      if (prevMode === "dark") return "system";
      return "light";
    });
  };

  // Select the appropriate theme based on actual theme
  const theme = actualTheme === "dark" ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider
      value={{ mode, setMode, toggleColorMode, actualTheme }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
